import { API_BASE } from "@/lib/api";

// Field telemetry for the TVs.
//
// Nobody can open DevTools on a Hisense VIDAA browser, so without this the
// only evidence of what a screen is doing is bandwidth graphs. Each event is
// a GET for a 1x1 GIF on the API, which folds it into per-screen counters and
// serves them back to the dashboard's Diagnostics tab.
//
// It originally pointed at the player's own path purely so the query string
// would land in the proxy's access log. That worked, but reading it meant an
// SSH session and a grep, which is no use to anyone running the screens.
//
// Events are sent only on state changes (boot, cache outcome, errors) plus a
// slow heartbeat, never on a tight timer, so they cost nothing measurable.

const screen = new URLSearchParams(window.location.search).get("screen") || "?";

export function diag(event: string, data: Record<string, string | number | boolean | undefined> = {}) {
  try {
    const params = new URLSearchParams({ e: event, s: screen, t: String(Date.now()) });
    for (const [k, v] of Object.entries(data)) {
      if (v !== undefined) params.set(k, String(v).slice(0, 160));
    }
    new Image().src = `${API_BASE}/diag?${params.toString()}`;
  } catch {
    // Telemetry must never affect playback.
  }
}

/** Once per page load: what this device's browser can actually do. */
export function reportCapabilities() {
  const v = document.createElement("video");
  diag("boot", {
    sw: "serviceWorker" in navigator,
    blob: typeof URL.createObjectURL === "function",
    mse: "MediaSource" in window,
    h264: v.canPlayType('video/mp4; codecs="avc1.640032"') || "no",
    mem: (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
    // Which address this screen was opened on. An http:// screen loses the
    // APIs that need a secure page, and cannot fetch its own video.
    proto: window.location.protocol.replace(":", ""),
    ua: navigator.userAgent.replace(/\s+/g, " "),
  });
}

/**
 * Prove the page is still alive, whatever it is showing.
 *
 * The video engine heartbeats while a video plays, but a screen configured
 * with images only has no video engine — it reported once at startup and then
 * went silent, which the dashboard could only read as "offline". A screen
 * showing a slideshow perfectly well must not look like a dead one.
 */
export function startHeartbeat(everyMs = 120_000) {
  setInterval(() => diag("alive", resourceStats()), everyMs);
}

interface PerfWithMemory extends Performance {
  memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number };
}
interface NavWithConnection extends Navigator {
  connection?: { downlink?: number; effectiveType?: string; saveData?: boolean };
}

/**
 * What this device is spending to show the screen.
 *
 * These TVs have 1 GB of RAM and no way to inspect them, so a screen that dies
 * every few hours looks identical to one that is fine until it goes. Memory
 * climbing towards the limit, or a link that has quietly dropped to 2G speeds,
 * is the difference between guessing and knowing.
 */
export function resourceStats(): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {
    up: Math.round(performance.now() / 1000),
  };

  // Chrome-only and heavily quantised, but these TVs are Chrome 111.
  const mem = (performance as PerfWithMemory).memory;
  if (mem && mem.jsHeapSizeLimit > 0) {
    out.heap = Math.round(mem.usedJSHeapSize / 1048576);
    out.heapmax = Math.round(mem.jsHeapSizeLimit / 1048576);
  }

  const conn = (navigator as NavWithConnection).connection;
  if (conn) {
    if (typeof conn.downlink === "number") out.dl = conn.downlink;
    if (conn.effectiveType) out.net = conn.effectiveType;
  }

  return out;
}

// A 1.5 KB, sixteen-pixel, tenth-of-a-second video. Nothing about it can be
// too large, too long or too demanding to decode, which is the point: if a
// device cannot play THIS from memory, it cannot play video from memory at
// all, and shrinking the real files would never have helped.
const TINY_MP4_BASE64 =
  "AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAMPbW9vdgAAAGxtdmhkAAAAAAAAAAAAAAAAAAAD6AAAAGQAAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAjl0cmFrAAAAXHRraGQAAAADAAAAAAAAAAAAAAABAAAAAAAAAGQAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAABAAAAAQAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAABkAAAAAAABAAAAAAGxbWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAAoAAAABABVxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAABXG1pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAARxzdGJsAAAAuHN0c2QAAAAAAAAAAQAAAKhhdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAABAAEABIAAAASAAAAAAAAAABFUxhdmM2MC4zMS4xMDIgbGlieDI2NAAAAAAAAAAAAAAAGP//AAAALmF2Y0MBQsAK/+EAFmdCwArZHsBEAAADAAQAAAMAUDxImSABAAVoy4PLIAAAABBwYXNwAAAAAQAAAAEAAAAUYnRydAAAAAAAAMjwAADI8AAAABhzdHRzAAAAAAAAAAEAAAABAAAEAAAAABxzdHNjAAAAAAAAAAEAAAABAAAAAQAAAAEAAAAUc3RzegAAAAAAAAKDAAAAAQAAABRzdGNvAAAAAAAAAAEAAAM/AAAAYnVkdGEAAABabWV0YQAAAAAAAAAhaGRscgAAAAAAAAAAbWRpcmFwcGwAAAAAAAAAAAAAAAAtaWxzdAAAACWpdG9vAAAAHWRhdGEAAAABAAAAAExhdmY2MC4xNi4xMDAAAAAIZnJlZQAAAottZGF0AAACcQYF//9t3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE2NCByMzEwOCAzMWUxOWY5IC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAyMyAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTAgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MToweDExMSBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MCBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0wIHdlaWdodHA9MCBrZXlpbnQ9MjUwIGtleWludF9taW49MTAgc2NlbmVjdXQ9NDAgaW50cmFfcmVmcmVzaD0wIHJjX2xvb2thaGVhZD00MCByYz1jcmYgbWJ0cmVlPTEgY3JmPTIzLjAgcWNvbXA9MC42MCBxcG1pbj0wIHFwbWF4PTY5IHFwc3RlcD00IGlwX3JhdGlvPTEuNDAgYXE9MToxLjAwAIAAAAAKZYiEDfJigAC2/g==";

/**
 * Settle, on the device itself, whether video can be played from memory.
 *
 * A browser reporting that it HAS the API tells us nothing — every screen says
 * yes and every screen then fails. This plays an actual video from an actual
 * in-memory copy and reports what happened.
 */
export async function testVideoFromMemory(timeoutMs = 15000) {
  let objectUrl = "";
  try {
    if (typeof URL.createObjectURL !== "function" || typeof atob !== "function") {
      diag("memvideo", { ok: false, why: "no-api" });
      return;
    }

    const binary = atob(TINY_MP4_BASE64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    objectUrl = URL.createObjectURL(new Blob([bytes], { type: "video/mp4" }));

    const started = Date.now();
    const result = await new Promise<{ ok: boolean; why: string }>((resolve) => {
      const probe = document.createElement("video");
      probe.muted = true;
      probe.preload = "metadata";
      probe.playsInline = true;
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1";
      document.body.appendChild(probe);

      let settled = false;
      const finish = (ok: boolean, why: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        probe.remove();
        resolve({ ok, why });
      };

      const timer = setTimeout(() => finish(false, "timeout-rs" + probe.readyState), timeoutMs);
      probe.onloadedmetadata = () => finish(true, "metadata");
      probe.onerror = () => finish(false, "error" + (probe.error ? probe.error.code : ""));
      probe.src = objectUrl;
      probe.load();
    });

    diag("memvideo", { ok: result.ok, why: result.why, ms: Date.now() - started });
  } catch (err) {
    diag("memvideo", { ok: false, why: String((err as Error)?.message || err).slice(0, 40) });
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

// The same clip in fragmented form, which is what MediaSource requires.
const TINY_FRAGMENTED_MP4_BASE64 =
  "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";

function base64ToBytes(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/**
 * The last caching route these TVs could have: MediaSource.
 *
 * They report supporting it, but they also reported supporting in-memory
 * playback and service workers, and neither does anything — so this measures
 * it rather than believing it. Feeds a 1.6 KB fragmented clip through
 * MediaSource and reports whether the element ever gets usable video.
 */
export async function testMediaSource(timeoutMs = 15000) {
  try {
    if (typeof MediaSource === "undefined" || typeof atob !== "function") {
      diag("msetest", { ok: false, why: "no-api" });
      return;
    }
    const mime = 'video/mp4; codecs="avc1.42c01e"';
    if (!MediaSource.isTypeSupported(mime)) {
      diag("msetest", { ok: false, why: "type-unsupported" });
      return;
    }

    const started = Date.now();
    const result = await new Promise<{ ok: boolean; why: string }>((resolve) => {
      const probe = document.createElement("video");
      probe.muted = true;
      probe.playsInline = true;
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1";
      document.body.appendChild(probe);

      const source = new MediaSource();
      const objectUrl = URL.createObjectURL(source);
      let settled = false;
      const finish = (ok: boolean, why: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        probe.remove();
        URL.revokeObjectURL(objectUrl);
        resolve({ ok, why });
      };
      const timer = setTimeout(() => finish(false, "timeout-rs" + probe.readyState), timeoutMs);

      probe.onloadedmetadata = () => finish(true, "metadata");
      probe.onerror = () => finish(false, "video-error" + (probe.error ? probe.error.code : ""));
      source.addEventListener("sourceopen", () => {
        try {
          const buffer = source.addSourceBuffer(mime);
          buffer.addEventListener("error", () => finish(false, "buffer-error"));
          buffer.appendBuffer(base64ToBytes(TINY_FRAGMENTED_MP4_BASE64));
        } catch (err) {
          finish(false, String((err as Error)?.name || err).slice(0, 30));
        }
      });
      // An object URL is how MediaSource is attached; if object URLs are the
      // broken part, this says so too, which is worth knowing either way.
      probe.src = objectUrl;
    });

    diag("msetest", { ok: result.ok, why: result.why, ms: Date.now() - started });
  } catch (err) {
    diag("msetest", { ok: false, why: String((err as Error)?.message || err).slice(0, 40) });
  }
}
